import { useMemo } from 'react';
import { makeKindKeyStringFromProps } from '@gonfalon/contexts/kind-key/makeKindKeyString';
import { anyOf, toString as queryfilterToString } from '@gonfalon/queryfilter/builder';
import { getContextsKindKeysQuery, getContextsWithNamesQuery } from '@gonfalon/rest-api/queries/contexts';
import { useQuery } from '@tanstack/react-query';

import type { ContextItemsEntities } from '#components/Contexts/types';
import {
  getContextsWithMissingNames,
  monkeyPatchContextItemsWithNames,
  toContextResponse,
} from '#components/Contexts/utils/contextTargetingUtils';

const RESOLVE_TARGETS_MAX_LIMIT = 50;

/**
 * Resolves the given `keys` (all of context kind `contextKind`) to their ContextItems via the batched
 * kind-key query, backfilling display names when the initial payload omits them. This is the React
 * Query equivalent of the contexts epic's `loadContextTargetBatch` (+ `fetchContextsWithNames`), and
 * reuses the same `getContextsWithMissingNames` / `monkeyPatchContextItemsWithNames` helpers.
 *
 * Only keys LaunchDarkly actually returned are present in the result map, so a missing entry means
 * "not seen" — callers can treat that as unresolved (e.g. render it unlinked), matching the epic path.
 */
export function useResolvedContextItems({
  projKey,
  envKey,
  contextKind,
  keys,
  enabled = true,
}: {
  projKey: string;
  envKey: string;
  contextKind: string;
  keys: string[];
  enabled?: boolean;
}): { itemsByKindKey: ContextItemsEntities; isLoading: boolean } {
  const filter = useMemo(
    () =>
      queryfilterToString(
        anyOf(
          'kindkey',
          keys.map((key) => `${contextKind}:${key}`),
        ),
      ),
    [keys, contextKind],
  );

  const { data: resolved, isFetching } = useQuery({
    ...getContextsKindKeysQuery({
      projectKey: projKey,
      environmentKey: envKey,
      query: { filter, sort: '-ts', limit: RESOLVE_TARGETS_MAX_LIMIT },
    }),
    enabled: enabled && keys.length > 0,
    select: toContextResponse,
  });

  const missingNameKindKeys = useMemo(() => (resolved ? getContextsWithMissingNames(resolved) : []), [resolved]);

  const { data: named, isFetching: namesFetching } = useQuery({
    ...getContextsWithNamesQuery({ projectKey: projKey, environmentKey: envKey, kindKeys: missingNameKindKeys }),
    enabled: enabled && missingNameKindKeys.length > 0,
    select: toContextResponse,
  });

  const itemsByKindKey = useMemo(() => {
    const map: ContextItemsEntities = {};
    if (!resolved) {
      return map;
    }
    const enriched = named && named.items.length > 0 ? monkeyPatchContextItemsWithNames(resolved, named) : resolved;
    enriched.items.forEach((item) => {
      map[makeKindKeyStringFromProps(item.context.kind, String(item.context.key))] = item;
    });
    return map;
  }, [resolved, named]);

  return { itemsByKindKey, isLoading: isFetching || namesFetching };
}
